/**
 * Algorithm 4 & 5: Question Selection
 * Main adaptive engine for selecting next question
 */

import { REINFORCEMENT, INTERVALS, PRIORITY, DECAY } from '@config/constants.js';

/**
 * Select the next question to present using adaptive algorithm
 * 
 * @param {Object} currentSection - Section user is studying
 * @param {Array} allSections - All section data with scores
 * @param {Array} allQuestions - All question data
 * @param {Array} sessionQuestions - Questions answered this session
 * @param {Object} options - Configuration options
 * @param {Object} options.tagMasteryMap - HamTestOnline tag mastery map for 60/30/10 weighting
 * @returns {Object} - { question, reason, sectionId }
 */
export function selectNextQuestion(currentSection, allSections, allQuestions, sessionQuestions, options = {}) {
  const { tagMasteryMap = {}, linearMode = false } = options;
  
  // In LINEAR mode (first pass), skip reinforcement priorities and jump straight to current section focus
  if (!linearMode) {
    // PRIORITY 1: Decay-triggered reinforcement (40% chance) - ADAPTIVE MODE ONLY
  const decayingSection = allSections.find(
    section =>
      section.index < currentSection.index && section.decayedAmount > DECAY.TRIGGER_THRESHOLD
  );

  if (decayingSection && Math.random() < REINFORCEMENT.DECAY_CHANCE) {
    console.log('✓ Selected DECAY reinforcement from', decayingSection.id);
    const q = selectQuestionFromSection(decayingSection, allQuestions);
    if (q) {
      return {
        question: q,
        reason: 'decay',
        sectionId: decayingSection.id,
      };
    }
  }

  // PRIORITY 2: Time-based review (20% chance)
  const sectionsDue = allSections.filter(
    section => section.index < currentSection.index && isSectionDueForReview(section)
  );

  if (sectionsDue.length > 0 && Math.random() < REINFORCEMENT.TIME_CHANCE) {
    const randomDueSection = sectionsDue[Math.floor(Math.random() * sectionsDue.length)];
    console.log('✓ Selected TIME review from', randomDueSection.id);
    const q = selectQuestionFromSection(randomDueSection, allQuestions);
    if (q) {
      return {
        question: q,
        reason: 'time',
        sectionId: randomDueSection.id,
      };
    }
  }

  // PRIORITY 3: Struggle-based reinforcement (30% chance)
  const recentCorrectRate = calculateRecentCorrectRate(
    sessionQuestions,
    REINFORCEMENT.RECENT_QUESTIONS_COUNT
  );
  if (
    recentCorrectRate < REINFORCEMENT.STRUGGLE_THRESHOLD &&
    currentSection.index > 0
  ) {
    const prereqSection = allSections[currentSection.index - 1];
    if (Math.random() < REINFORCEMENT.STRUGGLE_CHANCE) {
      console.log('✓ Selected STRUGGLE reinforcement from', prereqSection.id, '(correct rate:', recentCorrectRate.toFixed(2), ')');
      const q = selectQuestionFromSection(prereqSection, allQuestions);
      if (q) {
        return {
          question: q,
          reason: 'struggle',
          sectionId: prereqSection.id,
        };
      }
    }
  }

  // PRIORITY 4: Preview next section (DISABLED)
  // Preview is disabled to prevent showing questions from future sections
  // before those sections are explicitly unlocked and their content is shown.
  // This ensures proper sequential learning flow.

  } // End of ADAPTIVE mode reinforcement checks
  
  // PRIORITY 4: Adaptive mix based on HamTestOnline model
  // LINEAR MODE: 85% current section, 15% review from prior sections
  // ADAPTIVE MODE: 60% current section, 30% weak questions, 10% review questions
  // CRITICAL: Only include questions from current section or PRIOR sections (never future sections)
  const currentSectionQuestions = allQuestions.filter(q => q.sectionId === currentSection.id);
  
  // Only include questions from sections at or before current section index
  const otherSectionQuestions = allQuestions.filter(q => {
    const questionSection = allSections.find(s => s.id === q.sectionId);
    return questionSection && questionSection.index < currentSection.index;
  });
  
  // Separate candidates into current section and review pools
  const currentCandidates = [];
  const reviewCandidates = [];
  
  // Adjust distribution based on learning mode
  const distribution = linearMode 
    ? { current: 0.85, weak: 0.10, review: 0.05 }  // LINEAR: Focus heavily on current section
    : { current: 0.60, weak: 0.30, review: 0.10 }; // ADAPTIVE: Balanced review
  
  // Add questions from current section to currentCandidates
  if (currentSectionQuestions.length > 0) {
    const currentCount = Math.max(1, Math.ceil(currentSectionQuestions.length * distribution.current));
    const sorted = sortByPriority(currentSectionQuestions);
    currentCandidates.push(...sorted.slice(0, currentCount).map(item => ({...item.question, source: 'current'})));
  }
  
  // Add weak questions from other PRIOR sections to reviewCandidates
  const weakQuestions = otherSectionQuestions.filter(q => (q.currentScore || 0) < 70);
  if (weakQuestions.length > 0) {
    const weakCount = Math.max(1, Math.ceil(weakQuestions.length * distribution.weak));
    const sorted = sortByPriority(weakQuestions);
    reviewCandidates.push(...sorted.slice(0, weakCount).map(item => ({...item.question, source: 'weak_other'})));
  }
  
  // Add review questions (spaced repetition) from PRIOR sections to reviewCandidates
  const reviewQuestions = otherSectionQuestions.filter(q => (q.currentScore || 0) >= 70 && (q.currentScore || 0) < 90);
  if (reviewQuestions.length > 0) {
    const reviewCount = Math.max(1, Math.ceil(reviewQuestions.length * distribution.review));
    const sorted = sortByPriority(reviewQuestions);
    reviewCandidates.push(...sorted.slice(0, reviewCount).map(item => ({...item.question, source: 'review'})));
  }
  
  // If no candidates at all, fall back to current section
  if (currentCandidates.length === 0 && reviewCandidates.length === 0) {
    const q = selectQuestionFromSection(currentSection, allQuestions);
    return q
      ? {
          question: q,
          reason: 'current',
          sectionId: currentSection.id,
        }
      : null;
  }
  
  // FIXED: Prioritize current section questions first, then review questions
  // Strategy: Show ALL current section questions before ANY review questions
  // Count how many current section questions have been answered this session
  const answeredCurrentSectionIds = sessionQuestions
    .filter(sq => sq.questionId && allQuestions.find(q => q.id === sq.questionId && q.sectionId === currentSection.id))
    .map(sq => sq.questionId);
  
  // Find unanswered current section questions
  const unansweredCurrentQuestions = currentCandidates.filter(
    cand => !answeredCurrentSectionIds.includes(cand.id)
  );
  
  let selected;
  const modeLabel = linearMode ? 'LINEAR (85/10/5)' : 'ADAPTIVE (60/30/10)';
  
  if (unansweredCurrentQuestions.length > 0) {
    // Still have current section questions to show - select randomly from them
    selected = unansweredCurrentQuestions[Math.floor(Math.random() * unansweredCurrentQuestions.length)];
    console.log(`✓ Selected ${selected.source} question from ${modeLabel} mix - CURRENT section pool (${unansweredCurrentQuestions.length} remaining)`);
  } else if (reviewCandidates.length > 0) {
    // All current section questions answered - now show review questions
    selected = reviewCandidates[Math.floor(Math.random() * reviewCandidates.length)];
    console.log(`✓ Selected ${selected.source} question from ${modeLabel} mix - REVIEW pool (current section complete)`);
  } else {
    // Shouldn't reach here, but fallback
    const q = selectQuestionFromSection(currentSection, allQuestions);
    return q
      ? {
          question: q,
          reason: 'current',
          sectionId: currentSection.id,
        }
      : null;
  }
  
  return {
    question: selected,
    reason: selected.source === 'current' ? 'current' : 'review',
    sectionId: selected.sectionId,
  };
}

/**
 * Sort questions by priority score
 */
function sortByPriority(questions) {
  return questions
    .map(question => {
      let priority = 0;

      // FACTOR 1: Unseen questions (massive priority)
      if (question.timesShown === 0) {
        priority += PRIORITY.UNSEEN_QUESTION;
      }

      // FACTOR 2: Low-scoring questions (struggling)
      priority += (100 - question.currentScore) * PRIORITY.LOW_SCORE_MULTIPLIER;

      // FACTOR 3: Difficulty (harder questions prioritized)
      priority += question.difficulty * PRIORITY.DIFFICULTY_MULTIPLIER;

      // FACTOR 4: Overdue for repetition
      if (isQuestionDue(question)) {
        priority += PRIORITY.OVERDUE_BONUS;
      }

      // FACTOR 5: Recently seen penalty
      const minutesSinceLastSeen = (Date.now() - question.lastShown) / (1000 * 60);
      if (minutesSinceLastSeen < PRIORITY.RECENT_PENALTY_THRESHOLD) {
        priority *= PRIORITY.RECENT_PENALTY_MULTIPLIER;
      }

      return {
        question,
        priority,
      };
    })
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Select specific question from a section
 * 
 * @param {Object} section - Section to pull question from
 * @param {Array} allQuestions - All question data
 * @returns {Object} - Selected question
 */
export function selectQuestionFromSection(section, allQuestions) {
  // Get questions from this section
  const sectionQuestions = allQuestions.filter(q => q.sectionId === section.id);

  if (sectionQuestions.length === 0) {
    console.warn(`No questions available for section ${section.id} (all excluded or none unlocked)`);
    return null;
  }

  // Calculate priority score for each question
  const questionsWithPriority = sortByPriority(sectionQuestions);

  // Return highest priority question
  return questionsWithPriority[0].question;
}

/**
 * Check if section is due for review
 * 
 * @param {Object} section - Section data
 * @returns {boolean} - True if due for review
 */
export function isSectionDueForReview(section) {
  const hoursSinceStudy = (Date.now() - section.lastStudied) / (1000 * 60 * 60);

  // Find applicable interval threshold
  let requiredInterval = INTERVALS.SECTION[0]; // Default
  const thresholds = Object.keys(INTERVALS.SECTION)
    .map(Number)
    .sort((a, b) => a - b);

  for (const threshold of thresholds) {
    if (section.score >= threshold) {
      requiredInterval = INTERVALS.SECTION[threshold];
    }
  }

  return hoursSinceStudy >= requiredInterval;
}

/**
 * Check if individual question is due
 * 
 * @param {Object} question - Question progress data
 * @returns {boolean} - True if due for repetition
 */
export function isQuestionDue(question) {
  const minutesSinceLastSeen = (Date.now() - question.lastShown) / (1000 * 60);

  // Find applicable interval threshold
  let requiredInterval = INTERVALS.QUESTION[0]; // Default
  const thresholds = Object.keys(INTERVALS.QUESTION)
    .map(Number)
    .sort((a, b) => a - b);

  for (const threshold of thresholds) {
    if (question.currentScore >= threshold) {
      requiredInterval = INTERVALS.QUESTION[threshold];
    }
  }

  return minutesSinceLastSeen >= requiredInterval;
}

/**
 * Calculate recent performance
 * 
 * @param {Array} sessionQuestions - Questions answered this session
 * @param {number} count - Number of recent questions to check
 * @returns {number} - Correct rate (0-1)
 */
export function calculateRecentCorrectRate(sessionQuestions, count) {
  const recentQuestions = sessionQuestions.slice(-count);
  if (recentQuestions.length === 0) return 1.0;

  const correctCount = recentQuestions.filter(q => q.correct).length;
  return correctCount / recentQuestions.length;
}
